import {
  BulbOutlined,
  EditOutlined,
  FileTextOutlined,
  WhatsAppOutlined
} from "@ant-design/icons"
import { Card, QRCode, Typography } from "antd"
import React, { type ReactNode } from "react"
import { useTranslation } from "react-i18next"

const { Text, Title } = Typography

export function SettingsWhatsapp() {
  const { t } = useTranslation()
  const whatsappBotUrl = import.meta.env.VITE_APP_WHATSAPP_BOT_URL

  if (!whatsappBotUrl) return

  return (
    <Card classNames={{ body: "p-4" }}>
      {/* The card lives in the profile page's `lg:columns-2` grid, so its own
          width swings between a full-width phone column and half a desktop one
          — non-monotonic in the viewport, which is why the split below is a
          container query and not a `md:` breakpoint. */}
      <div className={"@container"}>
        <div
          data-testid={"whatsapp-assistant-layout"}
          className={"flex flex-col @min-[480px]:flex-row"}
        >
          <div
            className={
              "flex min-w-0 flex-col gap-3 @min-[480px]:basis-[65%] @min-[480px]:pr-5"
            }
          >
            <div className={"flex flex-wrap items-center gap-2"}>
              <Title level={5} className={"mb-0 font-semibold"}>
                {t("whatsappAssistant.title")}
              </Title>
              {/* WhatsApp's brand green only reaches 3.3:1 behind white text;
                  green-700 keeps the badge readable and still reads as
                  WhatsApp. */}
              <span
                className={
                  "inline-flex items-center gap-1 rounded-full bg-green-700 px-2 py-0.5 text-xs font-medium text-white"
                }
              >
                <WhatsAppOutlined aria-hidden />
                {t("whatsappAssistant.badge")}
              </span>
            </div>

            <Text className={"text-primary text-[15px] font-semibold"}>
              {t("whatsappAssistant.tagline")}
            </Text>

            <Text type={"secondary"} className={"text-xs"}>
              {t("whatsappAssistant.description")}
            </Text>

            <div className={"flex flex-col gap-2"}>
              <AssistantCapability
                icon={<FileTextOutlined />}
                title={t("whatsappAssistant.capabilities.getInformation.title")}
                detail={t(
                  "whatsappAssistant.capabilities.getInformation.detail"
                )}
              />
              <AssistantCapability
                icon={<EditOutlined />}
                title={t("whatsappAssistant.capabilities.updateData.title")}
                detail={t("whatsappAssistant.capabilities.updateData.detail")}
              />
            </div>

            <div
              className={
                "bg-primary/10 flex items-center gap-2 rounded-md px-2.5 py-1.5"
              }
            >
              <BulbOutlined
                aria-hidden
                className={"text-primary-strong text-sm"}
              />
              <Text className={"text-primary-strong text-xs"}>
                {t("whatsappAssistant.example")}
              </Text>
            </div>
          </div>

          <div
            className={
              "border-border mt-4 flex min-w-0 flex-col items-center gap-2 border-t pt-4 text-center @min-[480px]:mt-0 @min-[480px]:basis-[35%] @min-[480px]:border-t-0 @min-[480px]:border-l @min-[480px]:pt-0 @min-[480px]:pl-5"
            }
          >
            <Title level={5} className={"mb-0 font-semibold"}>
              {t("whatsappAssistant.connect.title")}
            </Title>
            <QRCode
              value={whatsappBotUrl}
              size={128}
              errorLevel="M"
              color="#000000"
              bgColor="#ffffff"
            />
            <Text type={"secondary"} className={"text-xs"}>
              {t("whatsappAssistant.connect.steps")}
            </Text>
            <Text type={"secondary"} className={"text-xs"}>
              {t("whatsappAssistant.connect.footer")}
            </Text>
          </div>
        </div>
      </div>
    </Card>
  )
}

function AssistantCapability({
  icon,
  title,
  detail
}: {
  icon: ReactNode
  title: string
  detail: string
}) {
  return (
    <div className={"flex items-center gap-2"}>
      <span
        aria-hidden
        className={
          "bg-primary/10 text-primary flex size-7 shrink-0 items-center justify-center rounded-md text-sm"
        }
      >
        {icon}
      </span>
      <div className={"flex min-w-0 flex-col"}>
        <Text className={"text-xs font-semibold"}>{title}</Text>
        <Text type={"secondary"} className={"text-xs"}>
          {detail}
        </Text>
      </div>
    </div>
  )
}
